import { ArrowLeft, Scale } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import {
  CONSERVATION_DATA_PROVENANCE,
  CONSERVATION_MODES,
  CONSERVATION_OF_MASS_TOOL_IDENTITY,
  DEFAULT_CONSERVATION_MODE,
  MODEL_BOUNDARIES,
  type ConservationMode,
} from './conservationOfMass';
import { MassReadingsMode } from './ConservationOfMassMassMode';
import { WordEquationMode } from './ConservationOfMassWordMode';
import { StudyShell } from './StudyShell';
import { toolsHubPath } from './studyTools';

const MODE_COPY: Record<ConservationMode, { tab: string; eyebrow: string; title: string; description: string }> = {
  word: {
    tab: 'Word equations',
    eyebrow: 'From words to formulae',
    title: 'Read a word equation and turn it into a balanced formula equation',
    description:
      'Sort the reactants and products, choose each formula, then balance the atoms so nothing is created or lost.',
  },
  mass: {
    tab: 'Mass readings',
    eyebrow: 'Open and closed systems',
    title: 'Predict the balance reading before and after a reaction',
    description:
      'Decide whether the mass goes up, down or stays the same, then find the gas that entered or left.',
  },
};

export function ConservationOfMassLabPage() {
  const [mode, setMode] = useState<ConservationMode>(DEFAULT_CONSERVATION_MODE);
  const copy = MODE_COPY[mode];

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'conservation-of-mass', state: { mode } }}>
      <Link
        to={toolsHubPath('chemistry')}
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-mint p-6 shadow-card-soft lg:p-8"
        data-tool-key={CONSERVATION_OF_MASS_TOOL_IDENTITY.toolKey}
        data-tool-version={CONSERVATION_OF_MASS_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
              <Scale className="h-4 w-4" aria-hidden="true" /> Reactions and equations
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Conservation of Mass Lab
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              In a chemical reaction, atoms are rearranged but never created or destroyed. Test that
              idea with equations and with balance readings.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Year 9+ · reactions and equations
          </div>
        </div>

        <div className="mt-6 grid max-w-md grid-cols-2 rounded-full bg-white/75 p-1" aria-label="Choose activity">
          {CONSERVATION_MODES.map(({ mode: option }) => (
            <button
              key={option}
              type="button"
              aria-pressed={mode === option}
              onClick={() => setMode(option)}
              className={`rounded-full px-3 py-2.5 text-sm font-extrabold transition-colors motion-reduce:transition-none ${
                mode === option ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {MODE_COPY[option].tab}
            </button>
          ))}
        </div>
      </header>

      <section className="my-6 rounded-[1.75rem] border border-hairline bg-canvas-pure px-5 py-4 shadow-card-soft sm:px-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">{copy.eyebrow}</p>
        <h2 className="mt-1 text-xl font-extrabold text-ink">{copy.title}</h2>
        <p className="mt-1 text-sm font-medium leading-6 text-slate2">{copy.description}</p>
      </section>

      {mode === 'word' ? <WordEquationMode /> : <MassReadingsMode />}

      <section className="mt-6 rounded-3xl border border-brand-sunshine/40 bg-wash-sunshine p-5" aria-label="Model boundaries">
        <p className="text-sm font-extrabold text-ink">Model boundaries and safety</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm font-medium leading-6 text-ink-soft">
          {MODEL_BOUNDARIES.map((boundary) => (
            <li key={boundary}>{boundary}</li>
          ))}
        </ul>
        <p className="mt-3 text-xs font-bold leading-5 text-slate2">
          {CONSERVATION_DATA_PROVENANCE.conditions} Sources: {CONSERVATION_DATA_PROVENANCE.sources.join('; ')}.
          Cases are pending teaching review.
        </p>
      </section>

      <footer className="my-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        This tool uses reviewed, bounded teaching cases. Nothing is saved. It does not replace a
        supervised practical, record an assessment attempt or claim alignment to a state syllabus.
      </footer>
    </StudyShell>
  );
}
